// El enlace profundo del correo de aviso (HU-10): `/casos/<uuid>`.
//
// El boton "Abrir el caso" del correo apunta a esa ruta, y el 28-ago-2026 daba
// 404 por DOS capas: el Static Web App no reescribia rutas desconocidas al
// index.html (arreglado en `public/staticwebapp.config.json`), y la SPA navega
// por estado interno — nunca leia la URL. Este modulo es la segunda mitad:
// reconocer la ruta al arrancar para que App lleve al lector directo al
// expediente de ese caso (tras el login simulado, que sigue siendo la puerta).

import { esUuid, PATRON_UUID } from './identificadores';

// El patron del uuid NO se escribe aqui: es el mismo que exige el cliente de la API
// antes de componer una URL (T-45), y dos copias de la misma regla divergen.
const RUTA_DE_CASO = new RegExp(`^/casos/(${PATRON_UUID})/?$`, 'i');

/** El uuid del caso si la ruta es `/casos/<uuid>`; null para cualquier otra. */
export function casoDesdeLaRuta(pathname: string | null | undefined): string | null {
  const casa = RUTA_DE_CASO.exec(pathname || '');
  return casa ? casa[1].toLowerCase() : null;
}

// ---------------------------------------------------------------------------------
// LA RUTA PROFUNDA DEL BORRADOR: `?vista=registro&caso=<uuid>` (BUG-370, MG-ILE-370).
//
// Recargar un borrador RETOMADO lo dejaba en blanco: el apuntador de la pestana solo se
// escribia al nacer el borrador, y aun con el, el registro montaba vacio. La ruta nombra
// en la barra de direcciones que borrador se esta registrando, y con ella la recarga (y
// el retorno del inicio de sesion, HU-32a-C5) vuelve a ESE borrador pidiendolo al
// servidor. Solo viaja el identificador: el contenido nunca sale del servidor por aqui.
//
// La forma `?vista=<vista>&caso=<id>` es la de las especificaciones del Sprint 3
// (`MG-ILE-43`, `MG-ILE-44`, `MG-ILE-63`); este es el primer uso, para la vista registro.
// Se escribe con `replaceState`, no con `pushState`: la SPA no tiene enrutador que
// atienda «Atras», y una entrada nueva en el historial cambiaria la URL sin cambiar la
// pantalla.
// ---------------------------------------------------------------------------------

const VISTA_DEL_BORRADOR = 'registro';

/** El uuid del borrador si la consulta es `?vista=registro&caso=<uuid>`; null si no. */
export function borradorDesdeLaRuta(search: string | null | undefined): string | null {
  const consulta = new URLSearchParams(search || '');
  const caso = consulta.get('caso');
  return consulta.get('vista') === VISTA_DEL_BORRADOR && esUuid(caso) ? caso.toLowerCase() : null;
}

/** Deja en la barra de direcciones la ruta profunda del borrador que se esta registrando. */
export function recordarRutaDelBorrador(casoId: string): void {
  if (!esUuid(casoId) || borradorDesdeLaRuta(window.location.search) === casoId.toLowerCase()) return;
  const consulta = new URLSearchParams({ vista: VISTA_DEL_BORRADOR, caso: casoId.toLowerCase() });
  // A la raiz de la aplicacion: si se llego por el enlace del correo (`/casos/<uuid>`),
  // conservar esa ruta haria que la recarga abriera el expediente y no el borrador.
  window.history.replaceState(window.history.state, '', `${import.meta.env.BASE_URL}?${consulta}`);
}

/** Quita la ruta profunda del borrador, si la hay. Cualquier otra ruta se deja como esta. */
export function olvidarRutaDelBorrador(): void {
  const consulta = new URLSearchParams(window.location.search);
  if (consulta.get('vista') !== VISTA_DEL_BORRADOR) return;
  consulta.delete('vista');
  consulta.delete('caso');
  const resto = consulta.toString();
  window.history.replaceState(
    window.history.state, '', `${window.location.pathname}${resto ? `?${resto}` : ''}${window.location.hash}`,
  );
}

// ---------------------------------------------------------------------------------
// LA RUTA PROFUNDA DE LA APROBACIÓN: `?vista=aprobacion&caso=<uuid>` (HU-17-C14, spec
// `ui/MG-ILE-43.md`, Pantalla). Es el enlace del correo de aviso al aprobador: abre el
// informe de ESE caso en la vista de aprobación tras el ingreso. Como la del borrador, solo
// viaja el identificador; lo demás lo pide la pantalla al servidor con la sesión de quien
// entra, y si no es aprobador el servidor lo dice (403, estado «Sin permiso»).
// ---------------------------------------------------------------------------------

const VISTA_DE_APROBACION = 'aprobacion';

/** El uuid del caso si la consulta es `?vista=aprobacion&caso=<uuid>`; null si no. */
export function aprobacionDesdeLaRuta(search: string | null | undefined): string | null {
  const consulta = new URLSearchParams(search || '');
  const caso = consulta.get('caso');
  return consulta.get('vista') === VISTA_DE_APROBACION && esUuid(caso) ? caso.toLowerCase() : null;
}

/** Quita la ruta profunda de la aprobación, ya consumida; cualquier otra ruta se deja como está. */
export function olvidarRutaDeLaAprobacion(): void {
  const consulta = new URLSearchParams(window.location.search);
  if (consulta.get('vista') !== VISTA_DE_APROBACION) return;
  consulta.delete('vista');
  consulta.delete('caso');
  const resto = consulta.toString();
  window.history.replaceState(
    window.history.state, '', `${window.location.pathname}${resto ? `?${resto}` : ''}${window.location.hash}`,
  );
}
